import { useEffect, useRef } from "react";

export interface ConfirmDialogProps {
  title: string;
  /** Key facts shown as a small label/value list. */
  rows?: { label: string; value: React.ReactNode }[];
  /** Highlighted heads-up, e.g. "This posts publicly on Instagram." */
  note?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Styles the confirm button as a destructive action. */
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Clean modal confirmation: summary, optional warning, Cancel + confirm. */
export default function ConfirmDialog({
  title,
  rows = [],
  note,
  confirmLabel,
  cancelLabel = "Cancel",
  danger = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef(onCancel);

  useEffect(() => {
    cancelRef.current = onCancel;
  });

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      className="ib-modal"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="ib-modal__panel">
        <h3 className="ib-modal__title">{title}</h3>
        {rows.length > 0 && (
          <dl className="ib-confirm__rows">
            {rows.map((r) => (
              <div key={r.label} className="ib-confirm__row">
                <dt>{r.label}</dt>
                <dd>{r.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {note && <div className="ib-confirm__note">{note}</div>}
        <div className="ib-migration-bar ib-modal__actions">
          <button
            type="button"
            className="jf-btn jf-btn-primary-outline ib-migration-btn"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`jf-btn ${
              danger ? "jf-btn-danger" : "jf-btn-primary"
            } ib-migration-btn`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
